import React, { useState } from 'react';
import { supabase } from '../supabase';
import AuthShell from './AuthShell';
import {
  EMAIL_RE,
  friendlyAuthError,
  inputStyle, inputFocusStyle, inputErrorStyle,
  labelStyle, inlineErrorStyle, primaryButtonStyle, linkStyle,
} from './authUtils';
import { colors, spacing, fontStack } from '../design-system/tokens';

/**
 * /login — Curx AI sign-in.
 *
 * Props:
 *   onLogin: (user) => void — App.js callback that sets user state
 *                             and redirects to "/".
 */
export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [focus, setFocus] = useState({ email: false, password: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const emailTrim = email.trim();
  const emailValid = EMAIL_RE.test(emailTrim);
  const passwordValid = password.length > 0;
  const formValid = emailValid && passwordValid;

  const showEmailError = touched.email && email.length > 0 && !emailValid;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!formValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: emailTrim,
        password,
      });
      if (error) throw error;
      onLogin && onLogin(data.user);
    } catch (err) {
      setError(friendlyAuthError(err?.message));
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <h1
        style={{
          margin: 0,
          fontSize: 28,
          fontWeight: 600,
          color: colors.textPrimary,
          letterSpacing: '-0.01em',
          fontFamily: fontStack,
        }}
      >
        Welcome back
      </h1>
      <p
        style={{
          margin: '6px 0 0',
          fontSize: 16,
          color: colors.textSecondary,
          fontFamily: fontStack,
        }}
      >
        Sign in to continue.
      </p>

      <form
        onSubmit={submit}
        noValidate
        style={{ marginTop: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.base }}
      >
        <div>
          <label htmlFor="login-email" style={labelStyle}>Email</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => { setTouched((t) => ({ ...t, email: true })); setFocus((f) => ({ ...f, email: false })); }}
            onFocus={() => setFocus((f) => ({ ...f, email: true }))}
            placeholder="you@example.com"
            required
            style={{
              ...inputStyle,
              ...(focus.email ? inputFocusStyle : null),
              ...(showEmailError ? inputErrorStyle : null),
            }}
          />
          {showEmailError && (
            <p style={inlineErrorStyle}>Please enter a valid email address.</p>
          )}
        </div>

        <div>
          <label htmlFor="login-password" style={labelStyle}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => { setTouched((t) => ({ ...t, password: true })); setFocus((f) => ({ ...f, password: false })); }}
              onFocus={() => setFocus((f) => ({ ...f, password: true }))}
              required
              style={{
                ...inputStyle,
                paddingRight: 44,
                ...(focus.password ? inputFocusStyle : null),
              }}
            />
            <EyeToggle
              shown={showPassword}
              onToggle={() => setShowPassword((v) => !v)}
            />
          </div>
        </div>

        <div style={{ textAlign: 'right', marginTop: -8 }}>
          <a
            href="/forgot-password"
            style={{ ...linkStyle, fontSize: 13 }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
          >
            Forgot password?
          </a>
        </div>

        {error && <p style={{ ...inlineErrorStyle, fontSize: 13, marginTop: 0 }}>{error}</p>}

        <button
          type="submit"
          disabled={!formValid || submitting}
          style={primaryButtonStyle(!formValid, submitting)}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p
        style={{
          margin: `${spacing.lg}px 0 0`,
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: 14,
          fontFamily: fontStack,
        }}
      >
        New to Curx?{' '}
        <a
          href="/signup"
          style={linkStyle}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
        >
          Create an account →
        </a>
      </p>
    </AuthShell>
  );
}

/* ================================================================
   Eye toggle — used by Login, Signup, ResetPassword
   ================================================================ */

export function EyeToggle({ shown, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={shown ? 'Hide password' : 'Show password'}
      style={{
        position: 'absolute',
        top: '50%',
        right: 8,
        transform: 'translateY(-50%)',
        width: 32,
        height: 32,
        padding: 0,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: colors.textSecondary,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
      }}
    >
      {shown ? <EyeOff /> : <Eye />}
    </button>
  );
}

function Eye() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.6" />
    </svg>
  );
}

function EyeOff() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3l18 18" />
      <path d="M10.6 6.2A9.5 9.5 0 0 1 12 6c6.5 0 10 6 10 6a18 18 0 0 1-3.5 4.4" />
      <path d="M6.7 7.5A18 18 0 0 0 2 12s3.5 6 10 6c1.6 0 3-.3 4.2-.9" />
      <path d="M9.5 9.5a3 3 0 1 0 4.2 4.2" />
    </svg>
  );
}
